import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAppRuntime } from "../providers/AppRuntimeProvider";
import { useRouterContext } from "../providers/RouterProvider";
import type { PaginatedResponse } from "./types";

export interface UseModuleListConfig<TRaw, T> {
  readonly rpcMethod: string;
  readonly queryKeyBase: readonly unknown[];
  readonly mapItem: (raw: TRaw) => T;
  readonly getId: (item: T) => string;
  readonly pageSize?: number;
  /** Extra params merged into every list RPC call */
  readonly extraParams?: Readonly<Record<string, unknown>>;
}

export interface UseModuleListResult<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly isLoading: boolean;
  readonly searchQuery: string;
  readonly setSearchQuery: (q: string) => void;
  readonly selectedId: string | undefined;
  readonly setSelectedId: (id: string | undefined) => void;
  readonly hasMore: boolean;
  readonly loadMore: () => void;
  readonly navigateTo: (id: string) => void;
  /** Patch a single item in the extra (page 2+) items list by ID. */
  readonly patchItem: (id: string, patch: Partial<T>) => void;
}

const DEFAULT_PAGE_SIZE = 50;

export function useModuleList<TRaw, T>(
  config: UseModuleListConfig<TRaw, T>,
): UseModuleListResult<T> {
  const { rpcMethod, queryKeyBase, mapItem, getId, pageSize = DEFAULT_PAGE_SIZE, extraParams } = config;
  const runtime = useAppRuntime();
  const router = useRouterContext();

  // --- Search state ---
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearch = useDeferredValue(searchQuery.trim());

  // --- TanStack Query for first page ---
  const queryParams = useMemo(() => {
    const params: Record<string, unknown> = { limit: pageSize, offset: 0, ...extraParams };
    if (deferredSearch) {
      params.search = deferredSearch;
    }
    return params;
  }, [pageSize, deferredSearch, extraParams]);

  const { data: queryData, isLoading: queryLoading } = useQuery({
    queryKey: [...queryKeyBase, "list", queryParams],
    queryFn: () =>
      runtime.transport.rpc<PaginatedResponse<TRaw>>(rpcMethod, queryParams),
    staleTime: 30_000,
  });

  // --- Map initial items ---
  const initialItems = useMemo(() => {
    if (!queryData) return [];
    return queryData.items.map(mapItem);
  }, [queryData, mapItem]);

  // --- Extra items from pagination ---
  const [extraItems, setExtraItems] = useState<T[]>([]);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);
  const offsetRef = useRef(0);

  // Rolling total from the LATEST page (live bug 2026-07-03 #4): search
  // implementations report total = offset+limit+1 while more rows exist (an
  // overfetch marker, not an exact count). Sticking to page 1's total froze
  // hasMore after the second page; each loadMore response refreshes it.
  const [pagedTotal, setPagedTotal] = useState<number | null>(null);

  // Reset extras when search changes
  useEffect(() => {
    setExtraItems([]);
    setPagedTotal(null);
    offsetRef.current = 0;
  }, [deferredSearch]);

  // --- Total ---
  const total = pagedTotal ?? queryData?.total ?? 0;

  // --- Merge + dedup ---
  // Extras merge in SEARCH MODE TOO (live bug 2026-07-03): the old
  // `if (isSearching) return initialItems` guard threw away every loadMore
  // page while searching, killing infinite scroll in search across ALL
  // modules. Cross-term pollution is already prevented by the reset-on-search
  // effect above; the dedup handles page overlap.
  const items = useMemo(() => {
    if (extraItems.length === 0) return initialItems;
    const seen = new Set(initialItems.map(getId));
    return [...initialItems, ...extraItems.filter((item) => !seen.has(getId(item)))];
  }, [initialItems, extraItems, getId]);

  // --- hasMore ---
  const hasMore = items.length < total;

  // --- loadMore ---
  const loadMore = useCallback(() => {
    if (loadMoreLoading || !hasMore) return;
    const newOffset = offsetRef.current + pageSize;
    offsetRef.current = newOffset;
    setLoadMoreLoading(true);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    void (async () => {
      try {
        const loadMoreParams: Record<string, unknown> = { limit: pageSize, offset: newOffset, ...extraParams };
        if (deferredSearch) {
          loadMoreParams.search = deferredSearch;
        }
        const result = await runtime.transport.rpc<PaginatedResponse<TRaw>>(
          rpcMethod,
          loadMoreParams,
        );
        const mapped = result.items.map(mapItem);
        setExtraItems((prev) => {
          const seen = new Set(prev.map(getId));
          return [...prev, ...mapped.filter((item) => !seen.has(getId(item)))];
        });
        setPagedTotal(result.total);
      } catch {
        // Keep existing items on error
      } finally {
        setLoadMoreLoading(false);
      }
    })();
  }, [loadMoreLoading, hasMore, pageSize, rpcMethod, runtime.transport, mapItem, getId, deferredSearch, extraParams]);

  // --- Selection ---
  const [selectedId, setSelectedIdRaw] = useState<string | undefined>();
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const setSelectedId = useCallback(
    (id: string | undefined) => {
      setSelectedIdRaw(id);
    },
    [],
  );

  // --- navigateTo ---
  // Tracks a pending navigate-to so we can set search+selection atomically
  // after the entity name is resolved.
  const pendingNavigateRef = useRef<string | null>(null);

  // Auto-select first item (skip if navigateTo is in progress)
  useEffect(() => {
    if (pendingNavigateRef.current) return;
    if (!selectedIdRef.current && items.length > 0) {
      const first = items[0];
      if (first) {
        setSelectedId(getId(first));
      }
    }
  }, [items, setSelectedId, getId]);

  const navigateTo = useCallback(
    async (id: string) => {
      pendingNavigateRef.current = id;
      // Fetch entity name first, THEN set search + selection together
      // to avoid intermediate empty-list flicker.
      try {
        const entity = await runtime.transport.rpc<{ name?: string }>(
          "graph.entity.get",
          { id },
        );
        // Only proceed if this is still the active navigation
        if (pendingNavigateRef.current !== id) return;
        if (entity.name) {
          setSearchQuery(entity.name);
        }
      } catch {
        // Not critical — still select the item
      }
      if (pendingNavigateRef.current === id) {
        setSelectedId(id);
        pendingNavigateRef.current = null;
      }
    },
    [setSelectedId, runtime.transport],
  );

  // --- Router integration: pendingItemId ---
  useEffect(() => {
    if (router.pendingItemId) {
      void navigateTo(router.pendingItemId);
      router.consumePendingItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pendingItemId]);

  // --- patchItem (for optimistic updates on page 2+ items) ---
  const patchItem = useCallback(
    (id: string, patch: Partial<T>) => {
      setExtraItems((prev) =>
        prev.map((item) => (getId(item) === id ? { ...item, ...patch } : item)),
      );
    },
    [getId],
  );

  // --- isLoading ---
  const isLoading = queryLoading || loadMoreLoading;

  return {
    items,
    total,
    isLoading,
    searchQuery,
    setSearchQuery,
    selectedId,
    setSelectedId,
    hasMore,
    loadMore,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    navigateTo,
    patchItem,
  };
}
